import type {
	FieldValidation,
	FormField,
	FormRule,
	FormRuleAction,
	FormRuleCondition,
	FormRuleConditionLogicValue,
	RuleEvaluationResult,
} from "../interfaces/dynamicForm.interface";
import {
	FormRuleActionType,
	FormRuleConditionLogic,
	FormRuleConditionOperator,
} from "../interfaces/dynamicForm.interface";

/**
 * Precomputed indexes for fast rule evaluation. Build once per
 * `(rules, fields)` change, reuse for every keystroke.
 *
 * @property fieldsByDefinitionId - O(1) lookup from a condition/action's `fieldDefinitionId` to the {@link FormField}.
 * @property rulesByTargetFieldId - All enabled rules that target a given field id (union of global rules and field-scoped `field.rules`).
 * @property dependencyIndex - For a given source `fieldDefinitionId`, the set of target field ids that may need re-evaluation when its value changes.
 * @property allTargetFieldIds - Every target field id mentioned by any enabled rule (used for initial evaluation pass).
 */
export interface RuleIndexes {
	fieldsByDefinitionId: Map<string, FormField>;
	rulesByTargetFieldId: Map<string, FormRule[]>;
	dependencyIndex: Map<string, Set<string>>;
	allTargetFieldIds: Set<string>;
}

const EMPTY_INDEXES: RuleIndexes = {
	fieldsByDefinitionId: new Map(),
	rulesByTargetFieldId: new Map(),
	dependencyIndex: new Map(),
	allTargetFieldIds: new Set(),
};

/** Walks groups/fieldArrays so nested fields are indexed too. */
function indexFieldsRecursive(
	fields: FormField[],
	out: Map<string, FormField>,
): void {
	for (const field of fields) {
		out.set(field.id, field);
		if (field.type === "group" || field.type === "fieldArray") {
			indexFieldsRecursive(field.fields, out);
		}
	}
}

/** Collects every rule that targets `targetFieldId` (field-scoped + global). */
function collectRulesForTarget(
	field: FormField,
	globalRules: FormRule[],
): FormRule[] {
	const out: FormRule[] = [];
	if (field.rules) {
		for (const r of field.rules) {
			if (r.enabled === false) continue;
			// Field-scoped rules apply to the host field by default unless an action says otherwise.
			out.push(r);
		}
	}
	for (const r of globalRules) {
		if (r.enabled === false) continue;
		if (r.actions.some((a) => a.targetFieldDefinitionId === field.id)) {
			out.push(r);
		}
	}
	return out;
}

/**
 * Builds the lookup indexes used by {@link evaluateFieldRules} and
 * {@link getDependentFields}.
 *
 * @param rules - Global rules from `schema.rules`.
 * @param allFields - Flat list of every field (including nested group/fieldArray children).
 */
export function buildRuleIndexes(
	rules: FormRule[] | undefined,
	allFields: FormField[],
): RuleIndexes {
	const fieldsByDefinitionId = new Map<string, FormField>();
	indexFieldsRecursive(allFields, fieldsByDefinitionId);

	const globalRules = (rules ?? []).filter((r) => r.enabled !== false);

	const rulesByTargetFieldId = new Map<string, FormRule[]>();
	const allTargetFieldIds = new Set<string>();
	const dependencyIndex = new Map<string, Set<string>>();

	for (const field of fieldsByDefinitionId.values()) {
		const targetedRules = collectRulesForTarget(field, globalRules);
		if (targetedRules.length === 0) continue;
		rulesByTargetFieldId.set(field.id, targetedRules);
		allTargetFieldIds.add(field.id);
	}

	for (const rule of globalRules) {
		const targets = new Set(rule.actions.map((a) => a.targetFieldDefinitionId));
		for (const condition of rule.conditions) {
			const sourceId = condition.fieldDefinitionId;
			let downstream = dependencyIndex.get(sourceId);
			if (!downstream) {
				downstream = new Set<string>();
				dependencyIndex.set(sourceId, downstream);
			}
			for (const t of targets) downstream.add(t);
		}
	}

	for (const field of fieldsByDefinitionId.values()) {
		if (!field.rules) continue;
		for (const rule of field.rules) {
			if (rule.enabled === false) continue;
			for (const condition of rule.conditions) {
				const sourceId = condition.fieldDefinitionId;
				let downstream = dependencyIndex.get(sourceId);
				if (!downstream) {
					downstream = new Set<string>();
					dependencyIndex.set(sourceId, downstream);
				}
				downstream.add(field.id);
			}
		}
	}

	return {
		fieldsByDefinitionId,
		rulesByTargetFieldId,
		dependencyIndex,
		allTargetFieldIds,
	};
}

/** Public sentinel for callers that need a no-op index (e.g. test setup). */
export function emptyRuleIndexes(): RuleIndexes {
	return EMPTY_INDEXES;
}

/**
 * Coerce string ↔ number where the condition value comes from the rule editor as a string
 * but the form field stores a number (and vice versa). Booleans/arrays pass through.
 */
function coerceForComparison(
	fieldValue: unknown,
	conditionValue: unknown,
): { left: unknown; right: unknown } {
	if (typeof fieldValue === "number" && typeof conditionValue === "string") {
		const n = Number(conditionValue);
		if (!Number.isNaN(n)) return { left: fieldValue, right: n };
	}
	if (typeof fieldValue === "string" && typeof conditionValue === "number") {
		const n = Number(fieldValue);
		if (!Number.isNaN(n)) return { left: n, right: conditionValue };
	}
	return { left: fieldValue, right: conditionValue };
}

function evaluateCondition(
	condition: FormRuleCondition,
	formValues: Record<string, unknown>,
	indexes: RuleIndexes,
): boolean {
	const field = indexes.fieldsByDefinitionId.get(condition.fieldDefinitionId);
	if (!field) return false;

	const fieldValue = formValues[field.name];
	const { left, right } = coerceForComparison(fieldValue, condition.value);

	switch (condition.operator) {
		case FormRuleConditionOperator.equals:
			return left === right;
		case FormRuleConditionOperator.notEquals:
			return left !== right;
		case FormRuleConditionOperator.contains:
			return (
				typeof left === "string" &&
				typeof right === "string" &&
				left.includes(right)
			);
		case FormRuleConditionOperator.notContains:
			return (
				typeof left === "string" &&
				typeof right === "string" &&
				!left.includes(right)
			);
		case FormRuleConditionOperator.greaterThan:
			return (
				typeof left === "number" && typeof right === "number" && left > right
			);
		case FormRuleConditionOperator.lessThan:
			return (
				typeof left === "number" && typeof right === "number" && left < right
			);
		case FormRuleConditionOperator.greaterThanOrEqual:
			return (
				typeof left === "number" && typeof right === "number" && left >= right
			);
		case FormRuleConditionOperator.lessThanOrEqual:
			return (
				typeof left === "number" && typeof right === "number" && left <= right
			);
		case FormRuleConditionOperator.isEmpty:
			if (Array.isArray(fieldValue)) return fieldValue.length === 0;
			return fieldValue === "" || fieldValue == null;
		case FormRuleConditionOperator.isNotEmpty:
			if (Array.isArray(fieldValue)) return fieldValue.length > 0;
			return fieldValue !== "" && fieldValue != null;
		case FormRuleConditionOperator.isChecked:
			return fieldValue === true;
		case FormRuleConditionOperator.isNotChecked:
			return fieldValue === false;
		case FormRuleConditionOperator.in:
			return (
				Array.isArray(condition.value) &&
				condition.value.includes(fieldValue as string)
			);
		case FormRuleConditionOperator.notIn:
			return (
				Array.isArray(condition.value) &&
				!condition.value.includes(fieldValue as string)
			);
		default:
			return false;
	}
}

function combineResults(
	conditions: FormRuleCondition[],
	logic: FormRuleConditionLogicValue,
	formValues: Record<string, unknown>,
	indexes: RuleIndexes,
): boolean {
	if (conditions.length === 0) return true;
	if (logic === FormRuleConditionLogic.any) {
		for (const c of conditions) {
			if (evaluateCondition(c, formValues, indexes)) return true;
		}
		return false;
	}
	for (const c of conditions) {
		if (!evaluateCondition(c, formValues, indexes)) return false;
	}
	return true;
}

function applyActionsToResult(
	actions: FormRuleAction[],
	targetFieldId: string,
	result: RuleEvaluationResult,
): void {
	for (const action of actions) {
		if (action.targetFieldDefinitionId !== targetFieldId) continue;
		switch (action.type) {
			case FormRuleActionType.showField:
				result.shouldShow = true;
				break;
			case FormRuleActionType.hideField:
				result.shouldShow = false;
				break;
			case FormRuleActionType.enableField:
				result.shouldEnable = true;
				break;
			case FormRuleActionType.disableField:
				result.shouldEnable = false;
				break;
			case FormRuleActionType.setRequired:
				result.isRequired = true;
				break;
			case FormRuleActionType.setOptional:
				result.isRequired = false;
				break;
			case FormRuleActionType.setValidation:
				if (
					action.value &&
					typeof action.value === "object" &&
					!Array.isArray(action.value)
				) {
					result.validation = action.value as FieldValidation;
				}
				break;
		}
	}
}

/**
 * Evaluates every rule targeting `field` and returns the merged
 * {@link RuleEvaluationResult}. Order: field static defaults → field-scoped rules
 * → global rules. Later rules override earlier ones for the same property.
 *
 * @param field - The field whose runtime state we want.
 * @param indexes - From {@link buildRuleIndexes}; pass {@link emptyRuleIndexes} for "no rules".
 * @param formValues - Current form values keyed by `field.name`.
 */
export function evaluateFieldRules(
	field: FormField,
	indexes: RuleIndexes,
	formValues: Record<string, unknown>,
): RuleEvaluationResult {
	const result: RuleEvaluationResult = {
		shouldShow: !field.hidden,
		shouldEnable: !field.disabled,
		isRequired: field.required || false,
		validation: undefined,
	};

	const targetedRules = indexes.rulesByTargetFieldId.get(field.id);
	if (!targetedRules || targetedRules.length === 0) return result;

	for (const rule of targetedRules) {
		const matched = combineResults(
			rule.conditions,
			rule.conditionLogic,
			formValues,
			indexes,
		);
		if (!matched) continue;
		applyActionsToResult(rule.actions, field.id, result);
	}

	return result;
}

/**
 * Returns the set of target field ids that may need re-evaluation when
 * `sourceFieldDefinitionId` changes. Always includes the source itself so
 * fields that gate on their own value still update.
 */
export function getDependentFields(
	sourceFieldDefinitionId: string,
	indexes: RuleIndexes,
): string[] {
	const downstream = indexes.dependencyIndex.get(sourceFieldDefinitionId);
	if (!downstream) return [sourceFieldDefinitionId];
	const set = new Set(downstream);
	set.add(sourceFieldDefinitionId);
	return Array.from(set);
}

/**
 * Static validation for a {@link FormRule}: reports unknown field references
 * and circular dependencies. Used by rule editor / persistence checks.
 */
export function validateRule(rule: FormRule, allFields: FormField[]): string[] {
	const errors: string[] = [];
	const fieldIds = new Set<string>();
	const collect = (fields: FormField[]) => {
		for (const f of fields) {
			fieldIds.add(f.id);
			if (f.type === "group" || f.type === "fieldArray") collect(f.fields);
		}
	};
	collect(allFields);

	for (const c of rule.conditions) {
		if (!fieldIds.has(c.fieldDefinitionId)) {
			errors.push(
				`Condition references non-existent field: ${c.fieldDefinitionId}`,
			);
		}
	}
	for (const a of rule.actions) {
		if (!fieldIds.has(a.targetFieldDefinitionId)) {
			errors.push(
				`Action targets non-existent field: ${a.targetFieldDefinitionId}`,
			);
		}
	}

	// Circular dependency walk scoped to this single rule's edges.
	const sources = rule.conditions.map((c) => c.fieldDefinitionId);
	const targets = rule.actions.map((a) => a.targetFieldDefinitionId);
	const adjacency = new Map<string, Set<string>>();
	for (const s of sources) {
		let set = adjacency.get(s);
		if (!set) {
			set = new Set<string>();
			adjacency.set(s, set);
		}
		for (const t of targets) set.add(t);
	}

	const visited = new Set<string>();
	const stack = new Set<string>();
	const dfs = (id: string): boolean => {
		if (stack.has(id)) {
			errors.push(`Circular dependency detected involving field: ${id}`);
			return true;
		}
		if (visited.has(id)) return false;
		visited.add(id);
		stack.add(id);
		const next = adjacency.get(id);
		if (next) {
			for (const n of next) {
				if (dfs(n)) {
					stack.delete(id);
					return true;
				}
			}
		}
		stack.delete(id);
		return false;
	};
	for (const t of targets) {
		if (dfs(t)) break;
	}

	return errors;
}
